import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useGameData } from '../hooks/useGameData';
import { Helmet } from 'react-helmet-async';

const FIRST_BAG_TICKS = [0, 12, 24, 36, 48, 60, 72, 84, 96];

// Discretionary ball removals the MLB API never reports (pitcher shake-offs,
// ump inspections, souvenir tosses). Calibrated as a FLAT per-game figure
// against 6 games with known real-world counts (true slop 1/14/16/16/26/32,
// mean 17.5, median 16) — slop does NOT scale with pitch count or events
// (|r| < 0.2), it is largely irreducible crew-behavior noise. 20 splits the
// difference between the accuracy-optimal ~17 and a safety-first 23 (leaning
// slightly high, since understating usage overstates remaining — the
// dangerous direction). No constant beats ~16 max error at this variance.
const SLOP_CONSTANT = 20;

const GameDetails = ({ gamePk, updateTotalBalls, umpSupply, ballSupply }) => {
  const { data } = useGameData(gamePk);

  const boxscoreData = data.data.liveData.boxscore;
  const away_pitches = boxscoreData.teams.away.teamStats.pitching.pitchesThrown || 0;
  const home_pitches = boxscoreData.teams.home.teamStats.pitching.pitchesThrown || 0;
  const total_pitches = away_pitches + home_pitches;

  const playData = useMemo(() => data.data.liveData.plays.allPlays || [], [data]);
  const total_plays = playData.length;

  const gameData = data.data.gameData;
  const home_team = gameData.teams.home.abbreviation;
  const away_team = gameData.teams.away.abbreviation;
  const game_date = gameData.datetime.officialDate;
  
  const liveData = data.data.liveData;
  const currentInning = liveData?.linescore?.currentInning || 1;
  const inningHalf = liveData?.linescore?.inningHalf || 'Top';
  const isFinal = gameData.status.abstractGameState === 'Final';
  // Each half-inning is 0.5 of a full inning
  const inningProgress = (currentInning - 1) + (inningHalf === 'Bottom' ? 1.0 : 0.5);

  const reformatAPIGameDate = game_date => {
    const dateParts = game_date.split('-');
    const day = dateParts[2];
    const month = dateParts[1];
    return `[${month}/${day}]`;
  };

  const reformattedDate = reformatAPIGameDate(game_date);

  // MEMOIZE PLAYS OUTPUT
  const playsOutput = useMemo(() => {
    const output = [];
    // Pitch outcomes where the ball leaves play or gets discarded
    const usedBallFilter = ['in play', 'foul', 'dirt', 'blocked', 'hit by pitch'];
    const isUsedBallPitch = description =>
      usedBallFilter.some(word => description.toLowerCase().includes(word));

    for (let i = 0; i < total_plays; i++) {
        const play = {};
        const ab = playData[i];
        const events = ab.playEvents || [];
        const playLength = events.length || 1;
        play.gamePk = gamePk;
        play.batter = ab.matchup.batter.id;
        play.batterName = ab.matchup.batter.fullName;
        play.batSide = ab.matchup.batSide.code;
        play.pitcher = ab.matchup.pitcher.id;
        play.pitcherName = ab.matchup.pitcher.fullName;
        play.pitchHand = ab.matchup.pitchHand.code;
        play.result = ab.result.event;
        play.inning = ab.about.inning;
        if (ab.about.halfInning === 'top') {
          play.batterTeam = away_team;
        } else {
          play.batterTeam = home_team;
        }
        play.halfInning = ab.about.halfInning;
        play.atBatIndex = ab.atBatIndex;
        play.playLength = playLength;
        play.id = `${play.gamePk}-${play.batter}-${play.pitcher}-${play.inning}-${play.atBatIndex}-${play.playLength}`;

        const prev = playData[i - 1];
        const isFirstPlayOfHalf =
          !prev ||
          prev.about.halfInning !== ab.about.halfInning ||
          prev.about.inning !== ab.about.inning;

        play.playEventDescriptions = [];
        play.usedBalls = [];

        // lastPitchCounted tracks whether the ball currently in play has already
        // been tallied, so follow-on rules (wild pitch, inning end, walk) don't
        // count the same ball twice
        let lastPitchCounted = false;
        let seenPitch = false;
        events.forEach(event => {
          const description = event.details && event.details.description;
          if (!description) return;
          play.playEventDescriptions.push(description);

          if (event.isPitch) {
            seenPitch = true;
            lastPitchCounted = isUsedBallPitch(description);
            if (lastPitchCounted) {
              play.usedBalls.push(description);
            }
            return;
          }

          const actionType = (event.details && event.details.event) || '';
          if ((actionType === 'Wild Pitch' || actionType === 'Passed Ball') && !lastPitchCounted) {
            play.usedBalls.push(actionType);
            lastPitchCounted = true;
          }
          // Between-innings changes (before the first pitch of a half-inning)
          // are covered by the normal inning-break ball below
          if (actionType === 'Pitching Substitution' && !(isFirstPlayOfHalf && !seenPitch)) {
            play.usedBalls.push('Pitching Change');
          }
        });

        // The ball in play is always retired when a half-inning ends; count it
        // unless the final pitch already did (covers strikeouts, pickoffs,
        // caught stealing, and challenge overturns ending an inning)
        if (ab.count.outs === 3 && !lastPitchCounted) {
          play.usedBalls.push('Inning-ending out');
        } else if (ab.result.event === 'Walk' && seenPitch && !lastPitchCounted) {
          // Pitchers toss out the ball after issuing a walk, unless ball four
          // was already counted (in the dirt)
          play.usedBalls.push('Walk');
        }

        output.push(play);
      }
      return output;
  }, [total_plays, playData, gamePk, home_team, away_team]);

  // MEMOIZE BALL DATA
  const ballData = useMemo(() => {
    const dataArr = [];
    playsOutput.forEach(play => {
        const { inning, batterTeam, usedBalls } = play;
        const inningEntry = dataArr.find(entry => entry.inning === inning);
        if (!inningEntry) {
          const newInningEntry = { inning, [home_team]: 0, [away_team]: 0, amt: 0 };
          newInningEntry[batterTeam] += usedBalls.length;
          newInningEntry.amt += usedBalls.length;
          dataArr.push(newInningEntry);
        } else {
          inningEntry[batterTeam] += usedBalls.length;
          inningEntry.amt += usedBalls.length;
        }
      });
      return dataArr;
  }, [playsOutput, home_team, away_team]);

  const usedBallEvents = useMemo(() => ballData.reduce((total, inningEntry) => total + inningEntry.amt, 0), [ballData]);
  // Accrue the flat game allowance across the game so the live meter and pace
  // don't absorb all of it on the first pitch; the calibrated full value
  // applies once the game is final.
  const slopFactor = total_pitches > 0
    ? Math.round(SLOP_CONSTANT * (isFinal ? 1 : Math.min(inningProgress / 9, 1)))
    : 0;

  const totalTelemetryUsed = usedBallEvents + slopFactor;
  const totalConsumed = (totalTelemetryUsed || 0) + (umpSupply || 0);
  const remainingBalls = (ballSupply || 0) - totalConsumed;
  
  // First Bag logic (Standard 96 balls)
  const firstBagValue = Math.max(0, 96 - totalConsumed);

  // Reserve Bag logic (Remaining supply - fixed 96 capacity visual)
  const reserveConsumed = Math.max(0, totalConsumed - 96);
  const reserveBagValue = Math.max(0, ((ballSupply || 180) - 96) - reserveConsumed);
  
  const telemetryPayload = useMemo(() => ({
    remaining: remainingBalls, 
    used: totalConsumed,
    firstBagMeter: firstBagValue,
    firstBagTicks: FIRST_BAG_TICKS,
    reserveBagMeter: reserveBagValue,
    reserveBagTicks: FIRST_BAG_TICKS,
    plays: playsOutput,
    ballData: ballData,
    homeTeam: home_team,
    awayTeam: away_team,
    currentInning: currentInning,
    gameStatus: gameData.status.abstractGameState
  }), [remainingBalls, totalConsumed, firstBagValue, reserveBagValue, playsOutput, ballData, home_team, away_team, currentInning, gameData.status.abstractGameState]);

  useEffect(() => {
    updateTotalBalls(telemetryPayload);
  }, [telemetryPayload, updateTotalBalls]);

  // Telemetry engine only — Home mounts this hidden, so render nothing but the
  // document title. Visible charts and last-play live in Home/LiveScoreboard.
  return (
    <Helmet>
      <title>{away_team}@{home_team} {reformattedDate}</title>
    </Helmet>
  );
};

const mapStateToProps = state => ({
  umpSupply: state.umpSupply,
  ballSupply: state.ballSupply,
});

export default connect(mapStateToProps)(GameDetails);
